import React, { useState } from 'react';
import './AiChatLandingPage.css';

const IMG_BASE = '/images/ai-chat';

// 단계별 전체 스크린샷 블록 아코디언
function ImageBlock({ src, alt }: { src: string; alt: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="img-block">
      <button
        className={`img-block-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span className="img-block-chevron">{open ? '▲' : '▼'}</span>
        {open ? '이미지 닫기' : '이미지 보기'}
      </button>
      {open && (
        <div className="img-block-content">
          <img src={src} alt={alt} className="img-block-img" />
        </div>
      )}
    </div>
  );
}

// 문장 중간 인라인 이모지 → 아코디언
function InlineImg({
  src, alt, id, openId, setOpenId,
}: {
  src: string; alt: string; id: string;
  openId: string | null; setOpenId: (id: string | null) => void;
}) {
  const isOpen = openId === id;
  return (
    <span className="inline-img-wrap">
      <button
        className={`inline-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setOpenId(isOpen ? null : id)}
        title={`${alt} 보기`}
      >🖼️</button>
      {isOpen && (
        <span className="inline-panel">
          <img src={src} alt={alt} className="inline-img" />
        </span>
      )}
    </span>
  );
}

const AiChatLandingPage: React.FC = () => {
  const [installing, setInstalling] = useState(false);
  const [inlineOpen, setInlineOpen] = useState<string | null>(null);

  const handleDownload = () => {
    setInstalling(true);
    const link = document.createElement('a');
    link.href = '/downloads/seah-ai-chat-installer.zip';
    link.download = 'seah-ai-chat-installer.zip';
    link.click();
    setTimeout(() => setInstalling(false), 2000);
  };

  // 인라인 이미지 헬퍼
  const ii = (id: string, alt: string) => (
    <InlineImg
      src={`${IMG_BASE}/${id}`}
      alt={alt}
      id={id}
      openId={inlineOpen}
      setOpenId={setInlineOpen}
    />
  );

  return (
    <div className="landing-root">

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-badge">Chrome Extension</div>
          <h1 className="landing-title">세아 정보보호 AI Chat</h1>
          <p className="landing-subtitle">
            정보보호 규정을 즉시 조회하고, 의심 메일을 30초 안에 분석하세요.
          </p>
          <div className="landing-cta-group">
            <button
              className={`landing-cta-btn ${installing ? 'loading' : ''}`}
              onClick={handleDownload}
              disabled={installing}
            >
              <span className="cta-icon">⬇</span>
              {installing ? '다운로드 중...' : 'Chrome에 추가'}
            </button>
            <a href="#how-to-install" className="landing-cta-link">설치 방법 보기 →</a>
          </div>
        </div>
        <div className="landing-hero-image">
          <div className="extension-mockup">
            <div className="mockup-header">
              <span className="mockup-dot red" />
              <span className="mockup-dot yellow" />
              <span className="mockup-dot green" />
              <span className="mockup-title">세아 정보보호 AI Chat</span>
            </div>
            <div className="mockup-body">
              <div className="mockup-msg user">개인정보 처리 방침 위반 시 어떻게 되나요?</div>
              <div className="mockup-msg ai">
                <span className="source-badge seah">세아 규정</span>
                개인정보보호법 위반 시 5년 이하 징역 또는 5천만원 이하 벌금이 부과될 수 있으며...
              </div>
              <div className="mockup-msg user">이 메일 안전한가요?</div>
              <div className="mockup-msg ai">
                <span className="source-badge critical">위험</span>
                발신 도메인이 paypa1.com으로 사칭이 탐지되었습니다. 열지 마세요.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features" id="features">
        <h2 className="section-title">주요 기능</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📋</div>
            <h3>AI 정보보호 규정 Q&amp;A</h3>
            <p>세아 내부 규정 문서와 KISA 가이드라인을 바탕으로 정확한 출처와 함께 맞춤형 답변을 제공합니다.</p>
            <ul className="feature-list">
              <li>세아 내부 규정 우선 검색</li>
              <li>KISA 정보보호 가이드라인 연계</li>
              <li>출처 명시 답변</li>
            </ul>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🛡️</div>
            <h3>악성메일 분석</h3>
            <p>Gmail에서 우클릭 한 번으로 의심 메일을 즉시 분석합니다. URL, 첨부파일, 발신 도메인을 종합 검사합니다.</p>
            <ul className="feature-list">
              <li>VirusTotal URL·파일 해시 조회</li>
              <li>도메인 사칭·이중 확장자 탐지</li>
              <li>AI 종합 위험도 판정</li>
            </ul>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Gmail 통합</h3>
            <p>Chrome 사이드바에서 Gmail을 보면서 동시에 사용합니다. 별도 탭 전환 없이 바로 분석 결과를 확인하세요.</p>
            <ul className="feature-list">
              <li>Gmail 사이드패널 연동</li>
              <li>우클릭 컨텍스트 메뉴</li>
              <li>세아 계정 자동 로그인</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 설치 방법 */}
      <section className="landing-install" id="how-to-install">
        <h2 className="section-title">설치 방법</h2>

        <div className="install-steps">

          {/* Step 1 */}
          <div className="install-step">
            <div className="step-num">1</div>
            <div className="step-body">
              <h3 className="step-title">다운로드 &amp; 압축 해제</h3>
              <p className="step-desc">
                <strong>"Chrome에 추가"</strong> 버튼 클릭 → ZIP 다운로드 →
                우클릭 → <strong>모두 압축 풀기</strong>
              </p>
              <ImageBlock src={`${IMG_BASE}/image_1.png`} alt="압축 해제 화면" />
            </div>
          </div>

          {/* Step 2 */}
          <div className="install-step">
            <div className="step-num">2</div>
            <div className="step-body">
              <h3 className="step-title">설치.bat 실행</h3>
              <p className="step-desc">
                압축 해제 폴더에서 <strong>설치.bat</strong> 더블클릭 →
                파일 복사 &amp; Chrome 자동 실행됨.
              </p>
              <p className="step-tip">💡 설치 경로가 클립보드에 자동 복사됩니다.</p>
              <ImageBlock src={`${IMG_BASE}/image_2.png`} alt="설치.bat 실행 화면" />
            </div>
          </div>

          {/* Step 3 */}
          <div className="install-step">
            <div className="step-num">3</div>
            <div className="step-body">
              <h3 className="step-title">Chrome에 로드</h3>
              <ol className="step-list">
                <li>
                  주소창에 <code>chrome://extensions</code> 입력해서 접속
                  {ii('image_3.png', 'chrome://extensions 접속 화면')}
                </li>
                <li>
                  우측 상단 <strong>개발자 모드</strong> 토글 ON
                  {ii('image_4.png', '개발자 모드 ON')}
                </li>
                <li>
                  <strong>압축 해제된 확장 프로그램 로드</strong> 버튼 클릭
                  {ii('image_5.png', '압축 해제된 확장 프로그램 로드 버튼')}
                </li>
                <li>
                  탐색기 상단 주소창에 <kbd>Ctrl+V</kbd> → <kbd>Enter</kbd> →
                  extension 폴더가 열리면 우측 하단 <strong>"폴더 선택"</strong> 클릭
                </li>
              </ol>
              <ImageBlock src={`${IMG_BASE}/image_6.png`} alt="폴더 선택 화면" />
            </div>
          </div>

          {/* Step 4 */}
          <div className="install-step">
            <div className="step-num">4</div>
            <div className="step-body">
              <h3 className="step-title">세아 계정으로 로그인</h3>
              <p className="step-desc">
                Chrome 우측 상단 확장 아이콘 클릭
                {ii('image_7.png', '확장 아이콘 위치')}
                <br />→ <strong>Google로 로그인</strong>
              </p>
              <p className="step-tip">
                🔐 <strong>vntgcorp.com</strong> / <strong>seah.co.kr</strong> 계정만 허용됩니다.
              </p>
              <ImageBlock src={`${IMG_BASE}/image_8.png`} alt="로그인 화면" />
            </div>
          </div>

        </div>
      </section>

      {/* Privacy */}
      <section className="landing-privacy">
        <h2 className="section-title">개인정보 보호</h2>
        <div className="privacy-grid">
          <div className="privacy-item">
            <span className="privacy-icon">🔑</span>
            <div>
              <strong>API 키 서버 관리</strong>
              <p>Gemini, VirusTotal API 키는 서버에서만 관리되며 Extension에 노출되지 않습니다.</p>
            </div>
          </div>
          <div className="privacy-item">
            <span className="privacy-icon">🙈</span>
            <div>
              <strong>채팅 내용 비식별화</strong>
              <p>질문 원문은 저장하지 않습니다. AI가 민감정보를 제거한 요약만 저장됩니다.</p>
            </div>
          </div>
          <div className="privacy-item">
            <span className="privacy-icon">🔒</span>
            <div>
              <strong>HTTPS 전용 통신</strong>
              <p>모든 통신은 HTTPS로 암호화되며 8시간 세션 타임아웃이 적용됩니다.</p>
            </div>
          </div>
          <div className="privacy-item">
            <span className="privacy-icon">🏢</span>
            <div>
              <strong>내부 전용</strong>
              <p>vntgcorp.com, seah.co.kr 도메인만 로그인 가능합니다. 외부 접근이 차단됩니다.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AiChatLandingPage;
